import { ButtonLink, cn, MapLandIcon } from '@landler/component-library';
import { FC, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useSearchParams } from 'react-router-dom';

import { OrganizationUIStyle } from '@/api/rest/resources/types/organization';
import { LandlerLogo } from '@/components';
import { CoPilot } from '@/components/CoPilot/CoPilot';
import { useDisplayCoPilot } from '@/components/CoPilot/hooks/useDisplayCoPilot';
import { Icon } from '@/components/widgets/a/Icon/Icon';
import { Sidebar, SidebarItem } from '@/components/widgets/o/Sidebar/Sidebar';
import { useMembershipType } from '@/hooks/useMembershipType';
import { usePathSet } from '@/hooks/usePathSet';
import { useUIStyle } from '@/hooks/useUIStyle';
import { buildPath } from '@/utils/buildPath';

type ContainerWithSidebarsProps = {
  children: ReactNode;
};

export const ContainerWithSidebars: FC<ContainerWithSidebarsProps> = ({ children }) => {
  const displayCoPilot = useDisplayCoPilot();
  const landlerNavProps = useDefaultLandlerSidebar();

  return (
    <div className='flex'>
      <Sidebar
        {...landlerNavProps}
        className='h-screen sticky top-0 p-6 rounded-none border border-neutral-black-12 gap-6 flex-shrink-0'
      />
      {/* Overflow visible is needed to avoid the chart tooltips from being cut off */}
      <div className='overflow-x-visible z-[1] w-full'>{children}</div>
      {displayCoPilot && <CoPilot />}
    </div>
  );
};

// TODO: MVP-CW - convert to widget properly, widget type + handler
// eslint-disable-next-line sonarjs/cognitive-complexity
export const useDefaultLandlerSidebar = () => {
  const membershipType = useMembershipType();
  const pathSet = usePathSet(membershipType);
  const { pathname } = useLocation();
  const { t } = useTranslation();

  const showGroupingPages = !useUIStyle(OrganizationUIStyle.CONSERVATION);

  const [searchParams] = useSearchParams();
  const queryParamPlotGroupId = searchParams.get('plotGroupId');
  const plotGroupQueryParams = queryParamPlotGroupId
    ? { queryParams: { plotGroupId: queryParamPlotGroupId } }
    : undefined;

  const createSidebarExpandableItem = (
    key: string,
    icon: string | ReactNode,
    label: string,
    children: SidebarItem[],
    to?: string,
  ): SidebarItem => ({
    key,
    content: to ? (
      <ButtonLink
        to={to}
        variant='text'
        className={cn(
          'text-text-primary justify-start grow rounded-md hover:rounded-md text-left leading-[130%] hover:bg-transparent',
          !icon && 'text-text-secondary',
        )}
      >
        {typeof icon === 'string' ? <Icon className='text-2xl/none' icon={icon} /> : icon}
        <span>{label}</span>
      </ButtonLink>
    ) : (
      <div
        className={cn(
          'flex items-center gap-2 w-full rounded-md text-left leading-[130%] px-5 py-3 typography-button1',
        )}
      >
        {typeof icon === 'string' ? <Icon className='text-2xl/none' icon={icon} /> : icon}
        <span>{label}</span>
      </div>
    ),
    children,
    isActive: pathname === to,
  });

  const createSidebarItem = (key: string, icon: string | ReactNode, label: string, to: string): SidebarItem => ({
    key,
    content: (
      <ButtonLink
        variant='text'
        to={to}
        className={cn(
          'text-text-primary justify-start w-full rounded-md hover:rounded-md text-left leading-[130%]',
          !icon && 'text-text-secondary',
          pathname === to && 'bg-neutral-hover text-text-primary',
        )}
      >
        {typeof icon === 'string' ? <Icon className='text-2xl/none' icon={icon} /> : icon}
        <span>{label}</span>
      </ButtonLink>
    ),
  });

  const createSideBarTextItem = (key: string, label: string): SidebarItem => ({
    key,
    content: <span className='typography-overline uppercase px-5 pt-4'>{label}</span>,
  });

  const navigationItems = [
    { key: 'dashboard', icon: 'ri-home-5-line', label: t('navbar.dashboard'), to: pathSet.dashboard },
    ...(showGroupingPages
      ? [
          {
            key: 'land',
            icon: <MapLandIcon className='w-6 h-6' />,
            label: t('navbar.land'),
            to: pathSet.plots,
            children: [
              createSidebarItem(
                'carbon',
                '',
                t('navbar.carbon'),
                buildPath(pathSet.carbonDetails, plotGroupQueryParams),
              ),
              createSidebarItem('water', '', t('navbar.water'), buildPath(pathSet.waterDetails, plotGroupQueryParams)),
              createSidebarItem(
                'biodiversity',
                '',
                t('navbar.biodiversity'),
                buildPath(pathSet.biodiversityDetails, plotGroupQueryParams),
              ),
              createSidebarItem('soil', '', t('navbar.soil'), buildPath(pathSet.soilDetails, plotGroupQueryParams)),
              createSidebarItem(
                'social',
                '',
                t('navbar.social'),
                buildPath(pathSet.socialDetails, plotGroupQueryParams),
              ),
            ],
          },
        ]
      : []),
    {
      key: 'portfolio',
      icon: 'ri-line-chart-line',
      label: t('navbar.portfolio'),
      to: undefined,
      children: [
        createSidebarItem('natureCapital', '', t('navbar.natureCapital'), pathSet.portfolioNaturalCapital),
        createSidebarItem('forecasts', '', t('navbar.forecasts'), pathSet.portfolioForecasts),
        createSidebarItem('investments', '', t('navbar.investments'), pathSet.portfolioInvestments),
        createSidebarItem('units', '', t('navbar.units'), pathSet.portfolioUnits),
      ],
    },
    { key: 'reports', icon: 'ri-file-text-line', label: t('navbar.reports'), to: pathSet.reports },
    { key: 'guidance', label: t('navbar.guidance'), isText: true, icon: '', to: '' },
    { key: 'copilot', icon: 'ri-record-circle-line', label: t('global.copilot.title'), to: pathSet.copilot },
    { key: 'knowledge', icon: 'ri-book-2-line', label: t('navbar.knowledge'), to: pathSet.knowledge },
    // TODO: MVP-CW bring these pages to the menu or completely remove them and their routes + components
    // { key: 'contracts', icon: 'ri-file-3-line', label: t('navbar.contracts'), to: pathSet.contracts },
    { key: 'guidance', label: t('navbar.settings'), isText: true, icon: '', to: '' },
    { key: 'indicator-library', icon: 'ri-leaf-line', label: t('navbar.indicators'), to: pathSet.indicators },
    { key: 'profile', icon: 'ri-user-line', label: t('navbar.profile'), to: pathSet.account },
  ];

  return {
    header: {
      content: <LandlerLogo className='h-9 w-[120px]' />,
      style: 'py-0',
    },
    items: navigationItems.map(({ key, icon, label, to, children, isText }) => {
      if (children && children.length > 0) {
        return createSidebarExpandableItem(key, icon, label, children, to);
      }
      if (isText) {
        return createSideBarTextItem(key, label);
      }

      return createSidebarItem(key, icon, label, to ?? '');
    }),
  };
};
